/**
 * API client + token storage + helpers.
 * Public endpoints don't require auth; admin endpoints attach Bearer token.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "byd_admin_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      tokenStore.clear();
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err.message || "Terjadi kesalahan";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

// Public endpoints
export const publicApi = {
  getSettings: () => api.get("/settings").then((r) => r.data),
  listPosts: () => api.get("/posts").then((r) => r.data),
  getPost: (slug) => api.get(`/posts/${slug}`).then((r) => r.data),
  listHeroSlides: () => api.get("/hero-slides").then((r) => r.data),
  listHandovers: () => api.get("/handovers").then((r) => r.data),
  getContent: () => api.get("/content").then((r) => r.data),
  getModelOverrides: () => api.get("/model-overrides").then((r) => r.data),
};

// Admin endpoints
export const adminApi = {
  login: (password) =>
    api.post("/auth/login", { password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (old_password, new_password) =>
    api
      .post("/auth/change-password", { old_password, new_password })
      .then((r) => r.data),

  // settings
  updateSettings: (data) => api.put("/settings", data).then((r) => r.data),

  // posts
  listPostsAdmin: () => api.get("/posts/admin").then((r) => r.data),
  createPost: (data) => api.post("/posts", data).then((r) => r.data),
  updatePost: (id, data) => api.put(`/posts/${id}`, data).then((r) => r.data),
  deletePost: (id) => api.delete(`/posts/${id}`).then((r) => r.data),

  // page seo
  listPageSeo: () => api.get("/page-seo").then((r) => r.data),
  upsertPageSeo: (page_key, data) =>
    api.put(`/page-seo/${page_key}`, data).then((r) => r.data),

  // files
  listFiles: () => api.get("/files").then((r) => r.data),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  deleteFile: (id) => api.delete(`/files/${id}`).then((r) => r.data),

  // hero slides
  listSlidesAdmin: () => api.get("/hero-slides/admin").then((r) => r.data),
  createSlide: (data) => api.post("/hero-slides", data).then((r) => r.data),
  updateSlide: (id, data) => api.put(`/hero-slides/${id}`, data).then((r) => r.data),
  deleteSlide: (id) => api.delete(`/hero-slides/${id}`).then((r) => r.data),

  // handovers
  listHandoversAdmin: () => api.get("/handovers/admin").then((r) => r.data),
  createHandover: (data) => api.post("/handovers", data).then((r) => r.data),
  updateHandover: (id, data) => api.put(`/handovers/${id}`, data).then((r) => r.data),
  deleteHandover: (id) => api.delete(`/handovers/${id}`).then((r) => r.data),

  // content K/V
  listContentAdmin: () => api.get("/content/admin").then((r) => r.data),
  upsertContent: (key, value) => api.put(`/content/${key}`, { value }).then((r) => r.data),

  // model overrides
  upsertModelOverride: (slug, data) =>
    api.put(`/model-overrides/${slug}`, data).then((r) => r.data),
  deleteModelOverride: (slug) =>
    api.delete(`/model-overrides/${slug}`).then((r) => r.data),
};

export function fileServeUrl(public_url_or_id) {
  if (!public_url_or_id) return "";
  // public_url returned from backend is a relative /api/files/serve/<id>
  if (public_url_or_id.startsWith("http")) return public_url_or_id;
  if (public_url_or_id.startsWith("/api/")) return `${BACKEND_URL}${public_url_or_id}`;
  return `${API_BASE}/files/serve/${public_url_or_id}`;
}
